import type { EnemyData, EnemyType, GameSprite } from "../../types"
import type { BaseEnemyRuntime, EnemyMovementHandler } from "../enemy-system"
import { BioPodEnemy } from "./bio-pod-enemy"
import { BomberEnemy } from "./bomber-enemy"
import { BrainEnemy } from "./brain-enemy"
import { ChainDrifterEnemy } from "./chain-drifter-enemy"
import { DwarfEnemy } from "./dwarf-enemy"
import { GunPodEnemy } from "./gun-pod-enemy"
import { HarvesterEnemy } from "./harvester-enemy"
import { HealerEnemy } from "./healer-enemy"
import { HornetDroneEnemy } from "./hornet-drone-enemy"
import { HumanoidEnemy } from "./humanoid-enemy"
import { KamikazeEnemy } from "./kamikaze-enemy"
import { MineDroneEnemy } from "./mine-drone-enemy"
import { NeonMoaiEnemy } from "./neon-moai-enemy"
import { OrbDroneEnemy } from "./orb-drone-enemy"
import { PiranhaEnemy } from "./piranha-enemy"
import { RouterEnemy } from "./router-enemy"
import { ScoutEnemy } from "./scout-enemy"
import { SnakeEnemy } from "./snake-enemy"
import { SnakePartEnemy } from "./snake-part-enemy"
import { SpikeMineEnemy } from "./spike-mine-enemy"
import { SynapseNodeEnemy } from "./synapse-node-enemy"

type EnemyFactory = (
  sprite: GameSprite,
  enemyData: EnemyData,
) => BaseEnemyRuntime

const ENEMY_FACTORIES: Partial<Record<EnemyType, EnemyFactory>> = {
  orbDrone: (sprite, enemyData) => new OrbDroneEnemy(sprite, enemyData),
  harvester: (sprite, enemyData) => new HarvesterEnemy(sprite, enemyData),
  humanoid: (sprite, enemyData) => new HumanoidEnemy(sprite, enemyData),
  router: (sprite, enemyData) => new RouterEnemy(sprite, enemyData),
  spikeMine: (sprite, enemyData) => new SpikeMineEnemy(sprite, enemyData),
  snake: (sprite, enemyData) => new SnakeEnemy(sprite, enemyData),
  snakeHead: (sprite, enemyData) => new SnakePartEnemy(sprite, enemyData),
  snakeBody: (sprite, enemyData) => new SnakePartEnemy(sprite, enemyData),
  snakeTail: (sprite, enemyData) => new SnakePartEnemy(sprite, enemyData),
  healer: (sprite, enemyData) => new HealerEnemy(sprite, enemyData),
  kamikaze: (sprite, enemyData) => new KamikazeEnemy(sprite, enemyData),
  brain: (sprite, enemyData) => new BrainEnemy(sprite, enemyData),
  synapseNode: (sprite, enemyData) => new SynapseNodeEnemy(sprite, enemyData),
  gunPod: (sprite, enemyData) => new GunPodEnemy(sprite, enemyData),
  bioPod: (sprite, enemyData) => new BioPodEnemy(sprite, enemyData),
  chainDrifter: (sprite, enemyData) => new ChainDrifterEnemy(sprite, enemyData),
  neonMoai: (sprite, enemyData) => new NeonMoaiEnemy(sprite, enemyData),
  dwarf: (sprite, enemyData) => new DwarfEnemy(sprite, enemyData),
  bomber: (sprite, enemyData) => new BomberEnemy(sprite, enemyData),
  piranha: (sprite, enemyData) => new PiranhaEnemy(sprite, enemyData),
  hornetDrone: (sprite, enemyData) => new HornetDroneEnemy(sprite, enemyData),
  mineDrone: (sprite, enemyData) => new MineDroneEnemy(sprite, enemyData),
  scout: (sprite, enemyData) => new ScoutEnemy(sprite, enemyData),
}

export function createEnemy(
  sprite: GameSprite,
  enemyData: EnemyData,
  movement: EnemyMovementHandler,
): BaseEnemyRuntime {
  const factory = ENEMY_FACTORIES[enemyData.type]
  if (factory) {
    const runtime = factory(sprite, enemyData)
    runtime.setBaseMovement(movement)
    return runtime
  }

  const { StatefulEnemyRuntime } = require("../enemy-system")

  class GenericEnemyRuntime extends (StatefulEnemyRuntime as any) {
    public constructor(sprite: GameSprite, enemyData: EnemyData) {
      const states: any[] = [
        {
          name: "active",
          behaviorState: "flying",
          animationState: "flying",
          update: (context: any) => {
            this.applyBaseMovement(context)
            return null
          },
        },
      ]
      super(sprite, enemyData, "active", states)
    }
  }

  const runtime = new GenericEnemyRuntime(sprite, enemyData) as any
  runtime.setBaseMovement(movement)
  return runtime
}
